import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Button from '@/components/Button';
import PasswordInput from '@/components/PasswordInput';
import PasswordRequirements from '@/components/PasswordRequirements';
import changePassIcon from '@/assets/change_pass.svg';
import changeSuccessIcon from '@/assets/change_success.svg';
import { resetPassword } from '@/api/auth';

const ResetPasswordDialog = ({ onClose, email, verificationCode }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const [verificationError, setVerificationError] = useState(null);
  
  const validatePassword = (password) => {
    return {
      length: password.length >= 8 && password.length <= 20,
      uppercase: /[A-Z]/.test(password),
      number: /[0-9]/.test(password),
    };
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    // Check password
    if (!password) {
      newErrors.password = t('auth.signup.errors.password_required');
    } else {
      const passwordValidation = validatePassword(password);
      if (!passwordValidation.length) {
        newErrors.password = t('auth.signup.errors.password_length');
      } else if (!passwordValidation.uppercase) {
        newErrors.password = t('auth.signup.errors.password_uppercase');
      } else if (!passwordValidation.number) {
        newErrors.password = t('auth.signup.errors.password_number');
      }
    }
    
    // Check confirm password
    if (!confirmPassword) {
      newErrors.confirmPassword = t('auth.signup.errors.confirm_password_required');
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = t('auth.signup.errors.passwords_not_match');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    try {
      await resetPassword(email, password, verificationCode);
      setIsSuccess(true);
    } catch (error) {
      console.error('Password reset failed:', error);
      
      // Handle specific error code for invalid verification code
      if (error.response && 
          error.response.status === 400 && 
          error.response.data?.code === 'invalid_verification_code') {
        // Automatically close the dialog and pass the error back to parent
        setVerificationError('invalid_verification_code');
        onClose({ verificationCodeError: 'invalid_verification_code' });
      } else {
        setErrors({
          general: t('user.errors.password_reset_failed') || 'Failed to reset password'
        });
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleConfirmSuccess = () => {
    navigate('/login');
  };
  
  const handleCloseResetDialog = () => {
    // If we have a verification code error, pass it back when closing
    if (errors.verificationCode) {
      onClose({ verificationCodeError: 'invalid_verification_code' });
    } else {
      onClose();
    }
  };
  
  if (isSuccess) {
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
        <div className="w-[360px] bg-white rounded-xl flex flex-col items-center overflow-hidden">
          <div className="self-stretch px-6 pt-8 pb-6 flex flex-col items-center gap-4">
            <div className="w-12 h-12 relative">
              <img src={changeSuccessIcon} alt="Success" className="w-full h-full" />
            </div>
            <div className="w-[263px] flex flex-col items-center gap-2">
              <div className="self-stretch text-center text-[#111111] text-lg font-bold leading-[27px]">
                {t('user.password.reset_success_title') || 'Password Reset Successful'}
              </div>
              <div className="self-stretch text-center text-[#666666] text-sm leading-[21px]">
                {t('user.password.reset_success_message') || 'Your password has been successfully reset. Please login with your new password.'}
              </div>
            </div>
          </div>
          <div className="self-stretch px-6 pb-6 bg-white flex flex-col justify-center items-center">
            <div className="self-stretch flex justify-start items-center gap-3">
              <Button 
                variant="primary" 
                className="flex-1 h-12 px-6 bg-[#111111] rounded-3xl"
                onClick={handleConfirmSuccess}
              >
                {t('auth.login.submit') || 'Login'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
      <div className="w-[360px] bg-white rounded-xl flex flex-col items-center overflow-hidden">
        <div className="self-stretch px-6 pt-8 pb-6 flex flex-col items-center gap-4">
          <div className="w-12 h-12 relative">
            <img src={changePassIcon} alt="Reset Password" className="w-full h-full" />
          </div>
          <div className="w-[263px] flex flex-col items-center gap-2">
            <div className="self-stretch text-center text-[#111111] text-lg font-bold leading-[27px]">
              {t('user.password.reset_title') || 'Reset Password'}
            </div>
          </div>
          <div className="self-stretch flex flex-col items-start gap-2">
            <div className="self-stretch flex flex-col items-start gap-1">
              <div className="self-stretch flex items-start gap-3">
                <div className="flex-1 flex items-center gap-1">
                  <div className="text-[#111111] text-sm leading-[21px]">
                    {t('user.password.new_password')}
                  </div>
                </div>
              </div>
              <PasswordInput
                placeholder={t('user.password.password_placeholder')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={errors.password}
                containerClassName="self-stretch"
                className="bg-[#f7f7f7] rounded-md outline-1 outline-offset-[-1px] outline-[#dddddd]"
              >
                <PasswordRequirements password={password} />
              </PasswordInput>
            </div>
          </div>
          <div className="self-stretch flex flex-col items-start gap-1">
            <div className="self-stretch flex items-start gap-3">
              <div className="flex-1 flex items-center gap-1">
                <div className="text-[#111111] text-sm leading-[21px]">
                  {t('user.password.confirm_password')}
                </div>
              </div>
            </div>
            <PasswordInput
              placeholder={t('user.password.confirm_placeholder')}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              error={errors.confirmPassword}
              containerClassName="self-stretch"
              className="bg-[#f7f7f7] rounded-md outline-1 outline-offset-[-1px] outline-[#dddddd]"
            />
          </div>
          {errors.general && (
            <div className="text-red-500 text-sm mt-2">
              {errors.general}
            </div>
          )}
        </div>
        <div className="self-stretch px-6 pb-6 bg-white flex flex-col justify-center items-center">
          <div className="self-stretch flex items-center gap-3">
            <Button 
              variant="default" 
              className="flex-1 h-12 px-6 bg-white rounded-3xl outline outline-[0.5px] outline-offset-[-0.5px] outline-[#dddddd]"
              onClick={handleCloseResetDialog}
            >
              {t('common.cancel')}
            </Button>
            <Button 
              variant="primary" 
              className="flex-1 h-12 px-6 bg-[#111111] rounded-3xl"
              onClick={handleSubmit}
              disabled={isLoading}
            >
              {isLoading ? t('common.loading') : t('common.confirm')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordDialog; 